import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator, Pressable } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { TrendingUp, Users, DollarSign, FileText, Calendar } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';

type TimePeriod = '30days' | '90days' | 'year' | 'all';

interface TimePeriodOption {
  value: TimePeriod;
  label: string;
}

const timePeriodOptions: TimePeriodOption[] = [
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

// Color palette for charts
const COLORS = {
  primary: '#000000',
  secondary: '#4b5563',
  success: '#16a34a',
  warning: '#ea580c',
  danger: '#dc2626',
  info: '#2563eb',
  purple: '#7c3aed',
  teal: '#0d9488',
  pink: '#db2777',
  chart1: '#000000',
  chart2: '#4b5563',
  chart3: '#9ca3af',
  chart4: '#d1d5db',
};

const PIE_COLORS = ['#000000', '#4b5563', '#9ca3af', '#d1d5db', '#e5e7eb'];

interface CustomerSuccessMetrics {
  totalUsers: number;
  userGrowth: { month: string; count: number }[];
  usersWithRequests: number;
  usersWithoutRequests: number;
  requestVolume: { month: string; count: number }[];
  lifecycleBreakdown: { stage: string; count: number }[];
  managerDistribution: { manager: string; count: number }[];
  growthRate: number;
}

interface FinancialMetrics {
  homeownerOutstanding: { amount: number; count: number };
  homeownerCollected: { amount: number; count: number };
  contractorOutstanding: { amount: number; count: number };
  contractorPaid: { amount: number; count: number };
  notInvoicedHomeowner: { amount: number; count: number };
  monthlyExpenses: {
    month: string;
    homeownerBilled: number;
    homeownerCollected: number;
    contractorPaid: number;
    netOutstanding: number;
  }[];
  quotesByStatus: { status: string; count: number; value: number }[];
}

const ReportsTab = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('year');
  const [loading, setLoading] = useState(true);
  const [customerMetrics, setCustomerMetrics] = useState<CustomerSuccessMetrics | null>(null);
  const [financialMetrics, setFinancialMetrics] = useState<FinancialMetrics | null>(null);

  useEffect(() => {
    fetchAllMetrics();
  }, [selectedPeriod]);

  const getDateFilter = (period: TimePeriod): Date | null => {
    const now = new Date();
    switch (period) {
      case '30days':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '90days':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case 'year':
        return new Date(now.getFullYear(), 0, 1);
      case 'all':
        return null;
    }
  };

  const fetchAllMetrics = async () => {
    setLoading(true);
    try {
      const [customerData, financialData] = await Promise.all([
        fetchCustomerSuccessMetrics(),
        fetchFinancialMetrics(),
      ]);
      setCustomerMetrics(customerData);
      setFinancialMetrics(financialData);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerSuccessMetrics = async (): Promise<CustomerSuccessMetrics> => {
    const dateFilter = getDateFilter(selectedPeriod);

    // Fetch all profiles
    let profilesQuery = supabase
      .from('profiles')
      .select('id, created_at, lifecycle_status, home_manager');

    if (dateFilter) {
      profilesQuery = profilesQuery.gte('created_at', dateFilter.toISOString());
    }

    const { data: profiles, error: profilesError } = await profilesQuery;
    if (profilesError) throw profilesError;

    // Fetch all homes to get owner_ids
    const { data: homes, error: homesError } = await supabase
      .from('homes')
      .select('id, owner_id');
    if (homesError) throw homesError;

    // Fetch service requests
    let requestsQuery = supabase
      .from('service_requests')
      .select('id, home_id, created_at');

    if (dateFilter) {
      requestsQuery = requestsQuery.gte('created_at', dateFilter.toISOString());
    }

    const { data: requests, error: requestsError } = await requestsQuery;
    if (requestsError) throw requestsError;

    // Calculate metrics
    const totalUsers = profiles?.length || 0;

    // User growth by month
    const monthlyGrowth: { [key: string]: number } = {};
    profiles?.forEach(profile => {
      const date = new Date(profile.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyGrowth[monthKey] = (monthlyGrowth[monthKey] || 0) + 1;
    });

    const userGrowth = Object.entries(monthlyGrowth)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => a.month.localeCompare(b.month));

    // Users with/without requests
    const homeOwnerMap = new Map(homes?.map(h => [h.owner_id, h.id]) || []);
    const usersWithRequestsSet = new Set(
      requests?.map(r => {
        const home = homes?.find(h => h.id === r.home_id);
        return home?.owner_id;
      }).filter(Boolean)
    );

    const usersWithRequests = usersWithRequestsSet.size;
    const usersWithoutRequests = totalUsers - usersWithRequests;

    // Request volume by month
    const monthlyRequests: { [key: string]: number } = {};
    requests?.forEach(request => {
      const date = new Date(request.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyRequests[monthKey] = (monthlyRequests[monthKey] || 0) + 1;
    });

    const requestVolume = Object.entries(monthlyRequests)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => a.month.localeCompare(b.month));

    // Lifecycle breakdown
    const lifecycleMap: { [key: string]: number } = {};
    profiles?.forEach(profile => {
      const stage = profile.lifecycle_status || 'lead';
      lifecycleMap[stage] = (lifecycleMap[stage] || 0) + 1;
    });

    const lifecycleBreakdown = Object.entries(lifecycleMap).map(([stage, count]) => ({
      stage: stage.charAt(0).toUpperCase() + stage.slice(1),
      count,
    }));

    // Manager distribution
    const managerMap: { [key: string]: number } = {};
    profiles?.forEach(profile => {
      const manager = profile.home_manager || 'Unassigned';
      managerMap[manager] = (managerMap[manager] || 0) + 1;
    });

    const managerDistribution = Object.entries(managerMap).map(([manager, count]) => ({
      manager,
      count,
    }));

    // Calculate growth rate (comparing first month to last month)
    let growthRate = 0;
    if (userGrowth.length >= 2) {
      const firstMonth = userGrowth[0].count;
      const lastMonth = userGrowth[userGrowth.length - 1].count;
      if (firstMonth > 0) {
        growthRate = ((lastMonth - firstMonth) / firstMonth) * 100;
      }
    }

    return {
      totalUsers,
      userGrowth,
      usersWithRequests,
      usersWithoutRequests,
      requestVolume,
      lifecycleBreakdown,
      managerDistribution,
      growthRate,
    };
  };

  const fetchFinancialMetrics = async (): Promise<FinancialMetrics> => {
    const dateFilter = getDateFilter(selectedPeriod);

    // Fetch expenses
    let expensesQuery = supabase
      .from('service_request_expenses')
      .select('id, amount, is_paid, payment_date, invoice_requested, invoice_id, created_at, billable_to_homeowner, needs_contractor_payment, contractor_paid, contractor_payment_date, parent_expense_id');

    if (dateFilter) {
      expensesQuery = expensesQuery.gte('created_at', dateFilter.toISOString());
    }

    const { data: expenses, error: expensesError } = await expensesQuery;
    if (expensesError) throw expensesError;

    const parentExpenses = expenses?.filter(e => !e.parent_expense_id) || [];

    // Calculate totals
    const homeownerOutstandingExpenses = parentExpenses.filter(e => e.billable_to_homeowner && !e.is_paid);
    const homeownerCollectedExpenses = parentExpenses.filter(e => e.billable_to_homeowner && e.is_paid);
    const contractorOutstandingExpenses = parentExpenses.filter(e => e.needs_contractor_payment && !e.contractor_paid);
    const contractorPaidExpenses = parentExpenses.filter(e => e.needs_contractor_payment && e.contractor_paid);
    const notInvoicedHomeownerExpenses = parentExpenses.filter(e => e.billable_to_homeowner && !e.invoice_requested && !e.invoice_id);

    const homeownerOutstanding = {
      amount: homeownerOutstandingExpenses.reduce((sum, e) => sum + (parseFloat(e.amount as any) || 0), 0),
      count: homeownerOutstandingExpenses.length,
    };

    const homeownerCollected = {
      amount: homeownerCollectedExpenses.reduce((sum, e) => sum + (parseFloat(e.amount as any) || 0), 0),
      count: homeownerCollectedExpenses.length,
    };

    const contractorOutstanding = {
      amount: contractorOutstandingExpenses.reduce((sum, e) => sum + (parseFloat(e.amount as any) || 0), 0),
      count: contractorOutstandingExpenses.length,
    };

    const contractorPaid = {
      amount: contractorPaidExpenses.reduce((sum, e) => sum + (parseFloat(e.amount as any) || 0), 0),
      count: contractorPaidExpenses.length,
    };

    const notInvoicedHomeowner = {
      amount: notInvoicedHomeownerExpenses.reduce((sum, e) => sum + (parseFloat(e.amount as any) || 0), 0),
      count: notInvoicedHomeownerExpenses.length,
    };

    // Monthly expense trends
    const monthlyData: {
      [key: string]: { homeownerBilled: number; homeownerCollected: number; contractorPaid: number };
    } = {};

    parentExpenses.forEach(expense => {
      const createdDate = new Date(expense.created_at);
      const monthKey = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { homeownerBilled: 0, homeownerCollected: 0, contractorPaid: 0 };
      }

      if (expense.billable_to_homeowner) {
        monthlyData[monthKey].homeownerBilled += parseFloat(expense.amount as any) || 0;
      }

      if (expense.billable_to_homeowner && expense.is_paid && expense.payment_date) {
        const paidDate = new Date(expense.payment_date);
        const paidMonthKey = `${paidDate.getFullYear()}-${String(paidDate.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyData[paidMonthKey]) {
          monthlyData[paidMonthKey] = { homeownerBilled: 0, homeownerCollected: 0, contractorPaid: 0 };
        }
        monthlyData[paidMonthKey].homeownerCollected += parseFloat(expense.amount as any) || 0;
      }

      if (expense.needs_contractor_payment && expense.contractor_paid && expense.contractor_payment_date) {
        const contractorPaidDate = new Date(expense.contractor_payment_date);
        const contractorPaidMonthKey = `${contractorPaidDate.getFullYear()}-${String(contractorPaidDate.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyData[contractorPaidMonthKey]) {
          monthlyData[contractorPaidMonthKey] = { homeownerBilled: 0, homeownerCollected: 0, contractorPaid: 0 };
        }
        monthlyData[contractorPaidMonthKey].contractorPaid += parseFloat(expense.amount as any) || 0;
      }
    });

    const sortedMonths = Object.keys(monthlyData).sort();
    let cumulativeNetOutstanding = 0;
    const monthlyExpenses = sortedMonths.map(month => {
      cumulativeNetOutstanding += monthlyData[month].homeownerBilled - monthlyData[month].homeownerCollected;
      cumulativeNetOutstanding += monthlyData[month].contractorPaid;
      return {
        month,
        homeownerBilled: monthlyData[month].homeownerBilled,
        homeownerCollected: monthlyData[month].homeownerCollected,
        contractorPaid: monthlyData[month].contractorPaid,
        netOutstanding: cumulativeNetOutstanding,
      };
    });

    // Fetch quotes
    let quotesQuery = supabase
      .from('service_request_quotes')
      .select('id, status, amount, created_at');

    if (dateFilter) {
      quotesQuery = quotesQuery.gte('created_at', dateFilter.toISOString());
    }

    const { data: quotes, error: quotesError } = await quotesQuery;
    if (quotesError) throw quotesError;

    // Quote breakdown by status
    const quoteStatusMap: { [key: string]: { count: number; value: number } } = {};
    quotes?.forEach(quote => {
      const status = quote.status || 'pending';
      if (!quoteStatusMap[status]) {
        quoteStatusMap[status] = { count: 0, value: 0 };
      }
      quoteStatusMap[status].count += 1;
      quoteStatusMap[status].value += parseFloat(quote.amount as any) || 0;
    });

    const quotesByStatus = Object.entries(quoteStatusMap).map(([status, data]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1),
      count: data.count,
      value: data.value,
    }));

    return {
      homeownerOutstanding,
      homeownerCollected,
      contractorOutstanding,
      contractorPaid,
      notInvoicedHomeowner,
      monthlyExpenses,
      quotesByStatus,
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatMonth = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading reports...</Text>
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.content}>
        {/* Time Period Filter */}
        <View style={styles.filterContainer}>
          <View style={styles.filterHeader}>
            <Calendar size={20} color={COLORS.secondary} />
            <Text style={styles.filterLabel}>Time Period:</Text>
          </View>
          <View style={styles.filterButtons}>
            {timePeriodOptions.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => setSelectedPeriod(option.value)}
                style={[
                  styles.filterButton,
                  selectedPeriod === option.value && styles.filterButtonActive,
                ]}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    selectedPeriod === option.value && styles.filterButtonTextActive,
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* Customer Success Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Users size={24} color={COLORS.primary} />
            <Text style={styles.sectionTitle}>Customer Success Metrics</Text>
          </View>

          {/* Growth Metrics */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Growth Overview</Text>
            <View style={styles.statsRow}>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>Total Users</Text>
                <Text style={styles.statValue}>{customerMetrics?.totalUsers || 0}</Text>
              </View>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>Growth Rate</Text>
                <Text style={[styles.statValue, { color: customerMetrics?.growthRate && customerMetrics.growthRate > 0 ? COLORS.success : COLORS.danger }]}>
                  {customerMetrics?.growthRate ? `${customerMetrics.growthRate.toFixed(1)}%` : '0%'}
                </Text>
              </View>
            </View>

            {customerMetrics?.userGrowth && customerMetrics.userGrowth.length > 0 && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>New Users Over Time</Text>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={customerMetrics.userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                      tick={{ fontSize: 12 }}
                      stroke={COLORS.secondary}
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke={COLORS.secondary} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                      labelFormatter={formatMonth}
                    />
                    <Bar dataKey="count" fill={COLORS.primary} name="New Users" />
                  </BarChart>
                </ResponsiveContainer>
              </View>
            )}
          </View>

          {/* Engagement Metrics */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>User Engagement</Text>
            <View style={styles.statsRow}>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>Users with Requests</Text>
                <Text style={styles.statValue}>{customerMetrics?.usersWithRequests || 0}</Text>
                <Text style={styles.statSubtext}>
                  {customerMetrics?.totalUsers
                    ? `${((customerMetrics.usersWithRequests / customerMetrics.totalUsers) * 100).toFixed(1)}%`
                    : '0%'}
                </Text>
              </View>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>Users without Requests</Text>
                <Text style={styles.statValue}>{customerMetrics?.usersWithoutRequests || 0}</Text>
                <Text style={styles.statSubtext}>
                  {customerMetrics?.totalUsers
                    ? `${((customerMetrics.usersWithoutRequests / customerMetrics.totalUsers) * 100).toFixed(1)}%`
                    : '0%'}
                </Text>
              </View>
            </View>

            {customerMetrics?.requestVolume && customerMetrics.requestVolume.length > 0 && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>Service Requests Volume</Text>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={customerMetrics.requestVolume}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                      tick={{ fontSize: 12 }}
                      stroke={COLORS.secondary}
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke={COLORS.secondary} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                      labelFormatter={formatMonth}
                    />
                    <Bar dataKey="count" fill={COLORS.info} name="Requests" />
                  </BarChart>
                </ResponsiveContainer>
              </View>
            )}
          </View>

          {/* Lifecycle Breakdown */}
          {customerMetrics?.lifecycleBreakdown && customerMetrics.lifecycleBreakdown.length > 0 && (
            <View style={styles.card}>
              <Text style={styles.cardTitle}>User Lifecycle Distribution</Text>
              <View style={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customerMetrics.lifecycleBreakdown}
                      dataKey="count"
                      nameKey="stage"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(entry) => `${entry.stage}: ${entry.count}`}
                    >
                      {customerMetrics.lifecycleBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }} />
                  </PieChart>
                </ResponsiveContainer>
              </View>
              <View style={styles.legendContainer}>
                {customerMetrics.lifecycleBreakdown.map((item, index) => (
                  <View key={item.stage} style={styles.legendItem}>
                    <View style={[styles.legendColor, { backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }]} />
                    <Text style={styles.legendText}>
                      {item.stage}: {item.count} ({((item.count / customerMetrics.totalUsers) * 100).toFixed(1)}%)
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Manager Distribution */}
          {customerMetrics?.managerDistribution && customerMetrics.managerDistribution.length > 0 && (
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Home Manager Distribution</Text>
              <View style={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={customerMetrics.managerDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="manager" tick={{ fontSize: 12 }} stroke={COLORS.secondary} />
                    <YAxis tick={{ fontSize: 12 }} stroke={COLORS.secondary} />
                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }} />
                    <Bar dataKey="count" fill={COLORS.purple} name="Users Assigned" />
                  </BarChart>
                </ResponsiveContainer>
              </View>
            </View>
          )}
        </View>

        {/* Financial Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <DollarSign size={24} color={COLORS.primary} />
            <Text style={styles.sectionTitle}>Financial & Cash Flow</Text>
          </View>

          {/* Expense Status Overview */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Two-Sided Expense Overview</Text>
            <View style={styles.statsRow}>
              <View style={[styles.statCard, styles.statCardDanger]}>
                <Text style={styles.statLabel}>Homeowner Outstanding</Text>
                <Text style={styles.statValue}>{formatCurrency(financialMetrics?.homeownerOutstanding.amount || 0)}</Text>
                <Text style={styles.statSubtext}>{financialMetrics?.homeownerOutstanding.count || 0} expenses</Text>
              </View>
              <View style={[styles.statCard, styles.statCardSuccess]}>
                <Text style={styles.statLabel}>Collected from Homeowners</Text>
                <Text style={styles.statValue}>{formatCurrency(financialMetrics?.homeownerCollected.amount || 0)}</Text>
                <Text style={styles.statSubtext}>{financialMetrics?.homeownerCollected.count || 0} expenses</Text>
              </View>
              <View style={[styles.statCard, styles.statCardWarning]}>
                <Text style={styles.statLabel}>Contractor Outstanding</Text>
                <Text style={styles.statValue}>{formatCurrency(financialMetrics?.contractorOutstanding.amount || 0)}</Text>
                <Text style={styles.statSubtext}>{financialMetrics?.contractorOutstanding.count || 0} expenses</Text>
              </View>
              <View style={[styles.statCard, styles.statCardSuccess]}>
                <Text style={styles.statLabel}>Paid to Contractors</Text>
                <Text style={styles.statValue}>{formatCurrency(financialMetrics?.contractorPaid.amount || 0)}</Text>
                <Text style={styles.statSubtext}>{financialMetrics?.contractorPaid.count || 0} expenses</Text>
              </View>
              <View style={[styles.statCard, styles.statCardWarning]}>
                <Text style={styles.statLabel}>Homeowner Not Invoiced</Text>
                <Text style={styles.statValue}>{formatCurrency(financialMetrics?.notInvoicedHomeowner.amount || 0)}</Text>
                <Text style={styles.statSubtext}>{financialMetrics?.notInvoicedHomeowner.count || 0} expenses</Text>
              </View>
            </View>
          </View>

          {/* Cash Flow Trends */}
          {financialMetrics?.monthlyExpenses && financialMetrics.monthlyExpenses.length > 0 && (
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Monthly Receivables and Payables</Text>
              <View style={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={financialMetrics.monthlyExpenses}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                      tick={{ fontSize: 12 }}
                      stroke={COLORS.secondary}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      stroke={COLORS.secondary}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                      labelFormatter={formatMonth}
                      formatter={(value: any) => formatCurrency(value)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="homeownerBilled" stroke={COLORS.info} strokeWidth={2} name="Homeowner Billed" />
                    <Line type="monotone" dataKey="homeownerCollected" stroke={COLORS.success} strokeWidth={2} name="Homeowner Collected" />
                    <Line type="monotone" dataKey="contractorPaid" stroke={COLORS.warning} strokeWidth={2} name="Contractor Paid" />
                    <Line
                      type="monotone"
                      dataKey="netOutstanding"
                      stroke={COLORS.danger}
                      strokeWidth={2}
                      name="Net Outstanding"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </View>

              {/* Monthly Details Table */}
              <View style={styles.tableContainer}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableHeaderText, styles.tableColMonth]}>Month</Text>
                  <Text style={[styles.tableHeaderText, styles.tableColAmount]}>Billed</Text>
                  <Text style={[styles.tableHeaderText, styles.tableColAmount]}>Collected</Text>
                  <Text style={[styles.tableHeaderText, styles.tableColAmount]}>Contractor Paid</Text>
                  <Text style={[styles.tableHeaderText, styles.tableColAmount]}>Net Outstanding</Text>
                </View>
                {financialMetrics.monthlyExpenses.map((row, index) => (
                  <View key={row.month} style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}>
                    <Text style={[styles.tableCellText, styles.tableColMonth]}>{formatMonth(row.month)}</Text>
                    <Text style={[styles.tableCellText, styles.tableColAmount]}>{formatCurrency(row.homeownerBilled)}</Text>
                    <Text style={[styles.tableCellText, styles.tableColAmount]}>{formatCurrency(row.homeownerCollected)}</Text>
                    <Text style={[styles.tableCellText, styles.tableColAmount]}>{formatCurrency(row.contractorPaid)}</Text>
                    <Text style={[styles.tableCellText, styles.tableColAmount, { color: COLORS.danger }]}>
                      {formatCurrency(row.netOutstanding)}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Quotes Overview */}
          {financialMetrics?.quotesByStatus && financialMetrics.quotesByStatus.length > 0 && (
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Quotes Overview</Text>
              <View style={styles.statsRow}>
                {financialMetrics.quotesByStatus.map((quote) => (
                  <View key={quote.status} style={styles.statCard}>
                    <Text style={styles.statLabel}>{quote.status} Quotes</Text>
                    <Text style={styles.statValue}>{quote.count}</Text>
                    <Text style={styles.statSubtext}>{formatCurrency(quote.value)} total</Text>
                  </View>
                ))}
              </View>
              <View style={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={financialMetrics.quotesByStatus}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="status" tick={{ fontSize: 12 }} stroke={COLORS.secondary} />
                    <YAxis tick={{ fontSize: 12 }} stroke={COLORS.secondary} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                      formatter={(value: any, name: string) =>
                        name === 'value' ? formatCurrency(value) : value
                      }
                    />
                    <Bar dataKey="count" fill={COLORS.teal} name="Count" />
                  </BarChart>
                </ResponsiveContainer>
              </View>
            </View>
          )}
        </View>
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    gap: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: 32,
  },
  loadingText: {
    fontSize: 16,
    color: COLORS.secondary,
  },
  filterContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 16,
    gap: 12,
    ...shadows.sm,
  },
  filterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.secondary,
  },
  filterButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  filterButtonActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.secondary,
  },
  filterButtonTextActive: {
    color: '#ffffff',
  },
  section: {
    gap: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.primary,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 20,
    gap: 16,
    ...shadows.sm,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.primary,
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: 150,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    gap: 4,
  },
  statCardDanger: {
    backgroundColor: '#fef2f2',
  },
  statCardSuccess: {
    backgroundColor: '#f0fdf4',
  },
  statCardWarning: {
    backgroundColor: '#fff7ed',
  },
  statLabel: {
    fontSize: 13,
    color: COLORS.secondary,
    fontWeight: '500',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.primary,
  },
  statSubtext: {
    fontSize: 12,
    color: COLORS.secondary,
  },
  chartContainer: {
    marginTop: 8,
    gap: 12,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.secondary,
  },
  legendContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 12,
    color: COLORS.secondary,
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
    marginTop: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.secondary,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableRowEven: {
    backgroundColor: '#f9fafb',
  },
  tableCellText: {
    fontSize: 14,
    color: COLORS.primary,
  },
  tableColMonth: {
    flex: 2,
  },
  tableColAmount: {
    flex: 1,
    textAlign: 'right',
  },
});

export default ReportsTab;

